import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  Inspection, InspectionType, CheckStatus, InspectionPhoto,
  User, Vehicle, VehiclePhotoPrompt, VehiclePhotoCapture
} from '@/types';
import { createFreshChecklist } from '@/utils/checklistTemplate';
import { PHOTO_TO_CHECKS } from '@/services/photoCheckMapper';

interface InspectionState {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;

  activeInspection: Inspection | null;
  startInspection: (vehicle: Vehicle, type: InspectionType, user: User, expectedVehicleDescription?: string) => void;
  cancelInspection: () => void;

  vehiclePhotos: VehiclePhotoCapture[];
  setVehiclePhoto: (promptId: VehiclePhotoPrompt, photo: InspectionPhoto) => void;
  clearVehiclePhotos: () => void;

  vehicleReferenceDescription: string | null;
  setVehicleReferenceDescription: (description: string) => void;

  expectedVehicleDescription: string | null;
  setExpectedVehicleDescription: (description: string) => void;

  currentCategoryIndex: number;
  setCurrentCategoryIndex: (idx: number) => void;

  setCheckStatus: (categoryId: string, checkId: string, status: CheckStatus, aiReasoning?: string) => void;
  setCheckNotes: (categoryId: string, checkId: string, notes: string) => void;
  addCheckPhoto: (categoryId: string, checkId: string, photo: InspectionPhoto) => void;
  removeCheckPhoto: (categoryId: string, checkId: string, photoId: string) => void;

  setOverallNotes: (notes: string) => void;
  setSignature: (dataUri: string) => void;
  setMileage: (mileage: number) => void;
  setInspectorFlagged: (flagged: boolean) => void;
  setFlagReason: (reason: string) => void;

  getCompletionStats: () => { total: number; completed: number; passed: number; failed: number; na: number };
  isReadyToSubmit: () => boolean;
}

export const useInspectionStore = create<InspectionState>()(
  persist(
    (set, get) => ({
      currentUser: null,
      setCurrentUser: (user) => set({ currentUser: user }),

      activeInspection: null,
      currentCategoryIndex: 0,
      vehiclePhotos: [],
      vehicleReferenceDescription: null,
      expectedVehicleDescription: null,

      startInspection: (vehicle, type, user, expectedVehicleDesc) => {
        const inspection: Inspection = {
          id: `insp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          vehicleId: vehicle.id,
          vehicleUnitNumber: vehicle.unitNumber,
          inspectorId: user.id,
          inspectorName: user.name,
          type,
          status: 'in-progress',
          startedAt: new Date().toISOString(),
          mileage: vehicle.mileage,
          categories: createFreshChecklist(type),
          overallNotes: '',
        };
        set({
          activeInspection: inspection,
          currentCategoryIndex: 0,
          vehicleReferenceDescription: null,
          expectedVehicleDescription: expectedVehicleDesc ?? null,
          vehiclePhotos: [],
        });
      },

      cancelInspection: () => set({
        activeInspection: null,
        currentCategoryIndex: 0,
        vehicleReferenceDescription: null,
        expectedVehicleDescription: null,
        vehiclePhotos: [],
      }),

      // ── Vehicle Photos ────────────────────────────────────────

      setVehiclePhoto: (promptId, photo) => set(state => {
        const existing = state.vehiclePhotos.find(p => p.promptId === promptId);
        if (existing) {
          return {
            vehiclePhotos: state.vehiclePhotos.map(p =>
              p.promptId === promptId ? { ...p, photo } : p
            ),
          };
        }
        return {
          vehiclePhotos: [...state.vehiclePhotos, { promptId, photo }],
        };
      }),

      clearVehiclePhotos: () => set({ vehiclePhotos: [] }),

      // ── Vehicle Reference ─────────────────────────────────────

      setVehicleReferenceDescription: (description) =>
        set({ vehicleReferenceDescription: description }),

      setExpectedVehicleDescription: (description) =>
        set({ expectedVehicleDescription: description }),

      // ── Navigation ────────────────────────────────────────────

      setCurrentCategoryIndex: (idx) => set({ currentCategoryIndex: idx }),

      // ── Check Updates ─────────────────────────────────────────

      setCheckStatus: (categoryId, checkId, status, aiReasoning) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        const categories = activeInspection.categories.map((cat) => {
          if (cat.id !== categoryId) return cat;
          return {
            ...cat,
            checks: cat.checks.map((ch) =>
              ch.id === checkId
                ? {
                    ...ch,
                    status,
                    ...(aiReasoning && status === 'fail' ? { aiReasoning } : {}),
                  }
                : ch
            ),
          };
        });
        set({ activeInspection: { ...activeInspection, categories } });
      },

      setCheckNotes: (categoryId, checkId, notes) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        const categories = activeInspection.categories.map((cat) => {
          if (cat.id !== categoryId) return cat;
          return {
            ...cat,
            checks: cat.checks.map((ch) => (ch.id === checkId ? { ...ch, notes } : ch)),
          };
        });
        set({ activeInspection: { ...activeInspection, categories } });
      },

      addCheckPhoto: (categoryId, checkId, photo) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        const categories = activeInspection.categories.map((cat) => {
          if (cat.id !== categoryId) return cat;
          return {
            ...cat,
            checks: cat.checks.map((ch) =>
              ch.id === checkId ? { ...ch, photos: [...ch.photos, photo] } : ch
            ),
          };
        });
        set({ activeInspection: { ...activeInspection, categories } });
      },

      removeCheckPhoto: (categoryId, checkId, photoId) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        const categories = activeInspection.categories.map((cat) => {
          if (cat.id !== categoryId) return cat;
          return {
            ...cat,
            checks: cat.checks.map((ch) =>
              ch.id === checkId
                ? { ...ch, photos: ch.photos.filter((p) => p.id !== photoId) }
                : ch
            ),
          };
        });
        set({ activeInspection: { ...activeInspection, categories } });
      },

      // ── Final Submission ──────────────────────────────────────

      setOverallNotes: (notes) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        set({ activeInspection: { ...activeInspection, overallNotes: notes } });
      },

      setSignature: (dataUri) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        set({ activeInspection: { ...activeInspection, signature: dataUri } });
      },

      setMileage: (mileage) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        set({ activeInspection: { ...activeInspection, mileage } });
      },

      setInspectorFlagged: (flagged) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        set({ activeInspection: { ...activeInspection, inspectorFlagged: flagged } });
      },

      setFlagReason: (reason) => {
        const { activeInspection } = get();
        if (!activeInspection) return;
        set({ activeInspection: { ...activeInspection, flagReason: reason } });
      },

      // ── Computed Helpers ──────────────────────────────────────

      getCompletionStats: () => {
        const { activeInspection } = get();
        if (!activeInspection) return { total: 0, completed: 0, passed: 0, failed: 0, na: 0 };

        let total = 0, completed = 0, passed = 0, failed = 0, na = 0;

        for (const cat of activeInspection.categories) {
          for (const ch of cat.checks) {
            if (ch.required) total++;
            if (ch.status !== null) {
              if (ch.required) completed++;
              if (ch.status === 'pass') passed++;
              else if (ch.status === 'fail') failed++;
              else if (ch.status === 'na') na++;
            }
          }
        }

        return { total, completed, passed, failed, na };
      },

      isReadyToSubmit: () => {
        const { activeInspection, vehiclePhotos } = get();
        if (!activeInspection) return false;

        const pendingCheckIds = new Set<string>();
        vehiclePhotos.forEach(vp => {
          if (vp.photo?.pendingUpload) {
            (PHOTO_TO_CHECKS[vp.promptId] ?? []).forEach(id => pendingCheckIds.add(id));
          }
        });

        for (const cat of activeInspection.categories) {
          for (const ch of cat.checks) {
            if (ch.required && ch.status === null && !pendingCheckIds.has(ch.id)) {
              return false;
            }
          }
        }

        if (!activeInspection.mileage || !activeInspection.signature) return false;
        if (activeInspection.inspectorFlagged && !activeInspection.flagReason?.trim()) return false;
        return true;
      },
    }),
    {
      name: 'wire3-inspection-store',
      storage: createJSONStorage(() => localStorage),

      // Only persist the fields needed to restore an in-progress inspection.
      // Exclude functions (they can't be serialized) and currentUser
      // (rehydrated from MSAL on login).
      partialize: (state) => ({
        activeInspection: state.activeInspection,
        currentCategoryIndex: state.currentCategoryIndex,
        expectedVehicleDescription: state.expectedVehicleDescription,
        vehiclePhotos: state.vehiclePhotos.map(vp => ({
          ...vp,
          photo: vp.photo ? {
            ...vp.photo,
            // Strip File objects — not serializable.
            // pendingUpload stays true so the user knows to retake if needed.
            pendingFile: undefined,
            // Blob URIs are session-only — clear them so stale URIs
            // don't show broken images after reload.
            uri: vp.photo.pendingUpload ? '' : vp.photo.uri,
          } : null,
        })),
      }),

      // After rehydration, mark any photos with empty URIs as needing retake
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Always reset the reference on reload — a stale description from a previous
        // session would cause every new photo to be rejected as "wrong vehicle".
        // The reference re-establishes itself from the first exterior photo taken.
        state.vehicleReferenceDescription = null;
        // Flag pending photos whose blob URI was lost in the reload
        state.vehiclePhotos = state.vehiclePhotos.map(vp => {
          if (vp.photo?.pendingUpload && !vp.photo.uri) {
            return {
              ...vp,
              photo: {
                ...vp.photo,
                uri: '',
                // Keep pendingUpload: true so the user sees ⏳ and knows to retake
              },
            };
          }
          return vp;
        });
      },
    }
  )
);